import { IStorageUser, OnlineStorage } from "@tblabs/storage";
import { HandlersCollection, RefString, RefBool, Snack } from "@tblabs/truffle";

export class LoginFormLogic
{
    private onLogin = new HandlersCollection<(userInfo: IStorageUser, autologin: boolean) => void>();
    private onLogout = new HandlersCollection<() => void>();
    public LoginInput = new RefString().Storable("auth-form:login-input");
    public PasswordInput = new RefString().Storable("auth-form:password-input");
    public ErrorMessageVisible = new RefBool();
    public LoginInProgress = new RefBool();
    public ErrorMessage = new RefString();

    constructor(private _storage: OnlineStorage)
    {
        this.PasswordInput.OnChange(() =>
        {
            this.ErrorMessageVisible.False();
        });

        this._storage.OnUserChange((user: IStorageUser, isAuthorized: boolean) =>
        {
            if (!isAuthorized)
            {
                this.onLogout.Call();
            }
        });
    }

    public get User(): IStorageUser
    {
        return this._storage.User;
    }

    public get IsAuthorized(): boolean
    {
        return this._storage.IsAuthorized;
    }

    public ClearForm(): void
    {
        this.LoginInput.value = "";
        this.PasswordInput.value = "";
        this.ErrorMessageVisible.False();
        this.LoginInProgress.False();
        this.ErrorMessage.value = "";
    }

    public ClearLoginInfo(): void
    {
        this.LoginInput.value = "";
        this.PasswordInput.value = "";
    }

    public OnLogin(handler: (userInfo: IStorageUser, autologin: boolean) => void): this
    {
        this.onLogin.Add(handler);

        return this;
    }

    public OnLogout(handler: () => void): this
    {
        this.onLogout.Add(handler);

        return this;
    }

    private autologinSnack?: Snack;
    private autologinFailedSnack?: Snack;

    public CleanUp(): void
    {
        this.autologinFailedSnack?.Close();
        this.autologinFailedSnack = undefined;
        this.autologinSnack?.Close();
        this.autologinSnack = undefined;
    }

    public Logout(): void
    {
        this._storage.Logout();
    }

    public async TryAutoLogin(): Promise<void>
    {
        // this.autologinSnack = new Snack("⌛ Autologin in progress", "Checking for credentials...", { autoCloseTimeout: 10000, clickAnywhereToClose: true });
        // console.log("TryAutoLogin", this._storage.IsNotLoggedIn ? "Not logged in" : "Logged in");
        try
        {
            if (this._storage.IsNotLoggedIn) // Autologujemy tylko kogoś kto jest w pamięci przeglądarki
            {
                await this._storage.Login(this.PasswordInput.value, this.LoginInput.value);
            }

            this.onLogin.Call(this._storage.User, true);
        }
        catch (ex: any)
        {
            const msg = ex.message.toLowerCase();
            let message;

            if (msg.includes("unauthorized"))
            {
                message = "Unauthorized";
            }
            else if (msg.includes("failed to fetch"))
            {
                message = "Connection problem";
            }
            else message = ex.message;

            this.autologinFailedSnack = new Snack("❌ Autologin failed", message, { autoCloseTimeout: 0, clickAnywhereToClose: true });
        }
        finally
        {
            this.autologinSnack?.Close();
            this.autologinSnack = undefined;
        }
    }

    public async TryLogin(): Promise<boolean>
    {
        if (this.LoginInProgress.value)
        {
            return false;
        }

        if (!this.PasswordInput.value)
        {
            this.ShowErrorMessage("No password provided");

            return false;
        }

        this.ErrorMessageVisible.False();
        this.LoginInProgress.True();

        try
        {
            await this._storage.Login(this.PasswordInput.value, this.LoginInput.value);

            this.LoginInProgress.False();
            this.CleanUp();

            this.onLogin.Call(this._storage.User, false);

            return true;
        }
        catch (ex: any)
        {
            this.LoginInProgress.False();
            this.ShowErrorMessage("Failed to login");

            return false;
        }
    }

    private ShowErrorMessage(message: string): void
    {
        this.ErrorMessage.value = message;
        this.ErrorMessageVisible.True();
    }
}
